import { MapPin, Clock, Car, Train } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { motion } from 'framer-motion';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

export default function Location() {
  const settings = useSiteSettings();
  return (
    <div data-testid="location-page" className="bg-white">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.5 }} className="max-w-3xl mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#F5F0F7] text-[#4B1D5E] text-sm font-medium mb-6">Location of Lessons</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2D1B36] mb-6">Location of Lessons</h1>
          <p className="text-base sm:text-lg text-[#5E4B66]">
            Lessons take place at the beautiful Bristol Cathedral Choir School in the heart of Bristol.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5 }}>
            <div className="rounded-3xl overflow-hidden shadow-lg mb-8 border border-gray-100">
              <iframe
                title="Bristol Cathedral Choir School Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2486.4!2d-2.5981!3d51.4521!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48718e78e0c6c3e3%3A0x2a95e2a34e0c1d0!2sBristol%20Cathedral%20Choir%20School!5e0!3m2!1sen!2suk!4v1700000000000"
                width="100%"
                height="350"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="location-map"
              />
            </div>
            <div className="rounded-3xl overflow-hidden shadow-lg">
              <img
                src={settings.images?.location || 'https://images.pexels.com/photos/35422693/pexels-photo-35422693.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'}
                alt="Bristol"
                className="w-full h-[250px] object-cover"
              />
            </div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5, delay: 0.15 }}>
            <Card className="border border-purple-100 rounded-2xl shadow-sm mb-6">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-[#F5F0F7] flex items-center justify-center shrink-0">
                    <MapPin className="h-6 w-6 text-[#4B1D5E]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-[#2D1B36] mb-1">Bristol Cathedral Choir School</h3>
                    <p className="text-sm text-[#5E4B66]">College Square, Bristol, BS1 5TS</p>
                  </div>
                </div>
                <p className="text-sm text-[#5E4B66] leading-relaxed">
                  The school is located in the historic heart of Bristol, next to Bristol Cathedral and College Green.
                  It provides a wonderful musical environment with excellent facilities for singing lessons.
                </p>
              </CardContent>
            </Card>

            <Card className="border border-purple-100 rounded-2xl shadow-sm mb-6">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#F5F0F7] flex items-center justify-center shrink-0">
                    <Clock className="h-6 w-6 text-[#4B1D5E]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-[#2D1B36] mb-1">Lesson Times</h3>
                    <p className="text-sm text-[#5E4B66]">Tuesday & Thursday evenings, 5:00 PM - 7:00 PM</p>
                  </div>
                </div>
                <p className="text-sm text-[#5E4B66] leading-relaxed">
                  Lessons are 30 minutes each. Please arrive 5 minutes before your scheduled time.
                </p>
              </CardContent>
            </Card>

            <Card className="border border-purple-100 rounded-2xl shadow-sm">
              <CardContent className="p-8 space-y-4">
                <h3 className="font-heading text-lg font-semibold text-[#2D1B36]">Getting Here</h3>
                <div className="flex items-start gap-3">
                  <Train className="h-5 w-5 text-[#B088BC] mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-sm text-[#2D1B36]">By Public Transport</p>
                    <p className="text-sm text-[#5E4B66]">Bristol Temple Meads station is a 15-minute walk. Several bus routes stop nearby on College Green.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Car className="h-5 w-5 text-[#B088BC] mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-sm text-[#2D1B36]">By Car</p>
                    <p className="text-sm text-[#5E4B66]">Nearby parking available on Deanery Road and College Street car park.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
